import { useCallback, useRef } from 'react';

import {
  claimSignOperation,
  defaultSignOperationDeps,
  idleSignOperation,
  resetSignOperation,
  settleSignOperation,
  type SignOperation,
  type SignOperationDeps,
  type SignOperationState,
} from '../services/sign-operation.service';

/**
 * React holder for the sign operation (`sign-operation.service.ts`) — the state
 * machine is pure and lives there; this owns nothing but the ref.
 *
 * ── WHY A REF, NOT `useState` ──────────────────────────────────────────────
 * The whole point is that TWO CLICKS INSIDE ONE REACT TICK must see each other.
 * `useState` would not have committed the first click's update before the second
 * click's handler read it — which is exactly how `accept.isPending` failed to stop
 * the double submit QA recorded. A ref is read and written synchronously, so the
 * second click observes the first. Nothing here is rendered, so there is no state
 * to render from.
 */
export interface SignOperationHandle {
  /**
   * Claim the operation for a submit. `undefined` means one is already in flight —
   * do nothing (NOT an error to show the customer).
   */
  claim(): SignOperation | undefined;
  /** The submit came back, either way. The operation is kept so a retry reuses it. */
  settle(): void;
  /** The customer changed the terms — the next claim begins a new acceptance. */
  reset(): void;
}

export function useSignOperation(
  deps: SignOperationDeps = defaultSignOperationDeps,
): SignOperationHandle {
  const state = useRef<SignOperationState>(idleSignOperation);

  const claim = useCallback((): SignOperation | undefined => {
    const result = claimSignOperation(state.current, deps);
    state.current = result.state;
    return result.claimed;
  }, [deps]);

  const settle = useCallback((): void => {
    state.current = settleSignOperation(state.current);
  }, []);

  const reset = useCallback((): void => {
    state.current = resetSignOperation();
  }, []);

  return { claim, settle, reset };
}
